<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import Icon from "./Icon.vue";
import { platformApi } from "../api/platform";
import { errorText } from "../api/errors";
import { toast } from "../composables/feedback";
import { session, workspace } from "../composables/session";
import { t } from "../i18n";

/**
 * While someone from Nuvvun is inside a customer's workspace through a support access, a pill at
 * the top says where, how (looking or adjusting) and until when, with the way out.
 */
const route = useRoute();
const router = useRouter();
const now = ref(Date.now());
let timer: ReturnType<typeof setInterval> | undefined;
onMounted(() => (timer = setInterval(() => (now.value = Date.now()), 30_000)));
onBeforeUnmount(() => clearInterval(timer));
const ws = computed(() =>
  route.meta.workspace && session.slug ? workspace(session.slug) : null,
);
const support = computed(() => ws.value?.support ?? null);
const over = computed(
  () =>
    !!support.value && new Date(support.value.expiresAt).getTime() <= now.value,
);
const time = computed(() =>
  support.value
    ? new Date(support.value.expiresAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "",
);
const busy = ref(false);
async function leave() {
  const w = ws.value;
  if (!w) return;
  busy.value = true;
  try {
    if (!over.value) await platformApi.endSupport(w.id);
    window.location.href = `/platform/customers/${w.id}`;
  } catch (err) {
    toast(errorText(err));
    busy.value = false;
    void router.push(`/platform/customers/${w.id}`);
  }
}
</script>
<template>
  <div v-if="support && ws" class="support-banner" role="status">
    <Icon name="shield" :size="16" />
    <span>{{
      over
        ? t("platform.support.ended")
        : t("support.banner", {
            name: ws.name,
            level: t(`support.level.${support.level}` as never),
            time,
          })
    }}</span>
    <button type="button" :disabled="busy" @click="leave">
      {{ t("support.leave") }}
    </button>
  </div>
</template>
